import React, { useContext, useState } from 'react';

import ChevronDown from '@gravity-ui/icons/ChevronDown';
import { SegmentedRadioGroup, TextArea, TextInput, withToaster } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import { updateDraft } from 'client/api';
import { SmartHomeControlPanel } from 'client/blocks/ControlPanel/SmartHomeControlPanel';
import { ErrorBoundary } from 'client/blocks/ErrorBoundary/ErrorBoundary';
import { AliceSkillLogoUploader } from 'client/blocks/FilesUploaders/AliceSkillLogoUploader';
import { BackendTypeField } from 'client/blocks/form/BackendTypeField/BackendTypeField';
import { EmailField } from 'client/blocks/form/EmailField/EmailField';
import { Field } from 'client/blocks/form/Field/Field';
import { FormCard } from 'client/blocks/form/FormCard/FormCard';
import { HostsSelect } from 'client/blocks/form/HostsSelect/HostsSelect';
import { InstallableAppAccessField } from 'client/blocks/form/InstallableAppAccessField/InstallableAppAccessField';
import { SkillUserAgreement } from 'client/blocks/SkillUserAgreement/SkillUserAgreement';
import { SmartHomeLanguagesEdit } from 'client/blocks/SmartHomeLanguageEdit/SmartHomeLanguagesEdit';
import { SmartHomeModeration } from 'client/blocks/SmartHomeModeration/SmartHomeModeration';
import { SmartHomeRegionsSelect } from 'client/blocks/SmartHomeRegionsSelect/SmartHomeRegionsSelect';
import { ModeratorNotes } from 'client/blocks/statuses/Notes/ModeratorNotes';
import { SettingsPageContext } from 'client/context/settingsPageContext';
import { WithSnapshot, withSnapshot } from 'client/context/snapshotContext';
import formContainer, { FormContainerProps } from 'client/hocs/form-container';
import { InjectedFormProps } from 'client/hocs/types';
import {
    lengthConfig,
    validateDescription,
    validateDeveloperName,
    validateEmail,
    validateLength,
    validateNote,
    validateSmartHomeName,
    validateURL,
} from 'client/lib/validation';
import { SmartHomeSettings } from 'client/model/settings';
import { Channel, SmartHome } from 'client/model/skill';
import { formUARequests, UserAgreement } from 'client/model/userAgreement';
import {
    ErrorForLanguageForm,
    LanguageCode,
    RegionCode,
    SmartHomeFormSettings,
    SupportedLanguageFormData,
} from 'client/types';
import { displayUserAgreements, getIsYangoRequest } from 'client/utils';
import { createUpdateError } from 'client/utils/errorMessage';
import { mapSmartHomeSettingsToValues, mapValuesToSmartHomeSettings } from 'client/utils/settings/smart-home';
import { addToast } from 'client/utils/settings/toaster';
import * as utils from 'client/utils/settings/utils';
import * as appFormKeyset from 'i18n/AppForm';
import * as TvAppIndexKeyset from 'i18n/AppsIndex';
import * as draftControlKeyset from 'i18n/DraftControlPanel';
import * as generalSettingsKeyset from 'i18n/GeneralSettings';
import * as keysetSettings from 'i18n/Settings';
import * as keysetSettingsLabels from 'i18n/SettingsLabels';
import * as keyset from 'i18n/SmartHomeForm';
import { isEqual } from 'lodash';

import styles from './Settings.module.scss';

const MIN_NAME_LENGTH = 2;

const t = i18n(keyset);
const tDraftControl = i18n(draftControlKeyset);
const tAppForm = i18n(appFormKeyset);
const tLabels = i18n(keysetSettingsLabels);
const tSettings = i18n(keysetSettings);
const tGeneralSettings = i18n(generalSettingsKeyset);
const tTvAppIndex = i18n(TvAppIndexKeyset);

interface OuterProps extends WithSnapshot {
    skillUseZora: boolean;
    disabled: boolean;
    displayEmailVerificationError: boolean;
    displayBrandVerificationError?: boolean;
    isDraft: boolean;
    settings: SmartHomeSettings;
    skill: SmartHome;
    refreshAliceApp?: () => void;
    fetchedUserAgreements?: UserAgreement[];
}

type Props = InjectedFormProps<SmartHomeFormSettings, OuterProps>;

const SmartHomeFormFields: React.FC<Props> = ({
    values,
    errors,
    onChange,
    disabled,
    isDraft,
    skill,
    snapshot,
    displayEmailVerificationError,
    displayBrandVerificationError,
    isSubmitting,
    touched,
    isNonValidationError,
    refreshAliceApp,
    setErrors,
}) => {
    const [openCardName, setOpenCardName] = useState('');

    const { hasUnsavedChanges, setHasUnsavedChanges } = useContext(SettingsPageContext);

    const fromDisabled = disabled || isSubmitting;
    const isYangoRequest = getIsYangoRequest();

    const handleMobileCardClick = (tabName: string) => {
        if (tabName === openCardName) {
            setOpenCardName('');
        } else {
            setOpenCardName(tabName);
        }
    };

    return (
        <ErrorBoundary withHomepageLink>
            <header className={styles.header}>
                <SmartHomeControlPanel
                    settingValues={values}
                    isSubmitting={isSubmitting}
                    settingFormErrors={errors}
                    setErrors={setErrors}
                    isNonValidationError={isNonValidationError}
                    appName={skill.onAir ? skill.name : skill.draft.name}
                    logoUrl={isDraft ? skill.draft.logo2?.url : skill.logo2?.url}
                />
                {isDraft && <ModeratorNotes />}
            </header>
            <div className={styles.mobileFormHeader}>
                <ChevronDown />
                {tTvAppIndex('main-information')}
            </div>
            <div className={styles.smartHomeFormContainer}>
                <div>
                    <FormCard
                        cardKey="about"
                        cardTitle={t('language-and-description')}
                        handleOpenCard={handleMobileCardClick}
                        openCardName={openCardName}
                    >
                        <SmartHomeLanguagesEdit
                            isDraft={isDraft}
                            isTrustedSmartHomeSkill={values.isTrustedSmartHomeSkill}
                            languages={values.supportedLanguages}
                            serializedErrorsForLanguage={errors.supportedLanguages || []}
                            setSupportedLanguages={onChange('supportedLanguages')}
                            disabled={fromDisabled || !isDraft}
                        />

                        <SmartHomeRegionsSelect
                            languages={values.supportedLanguages}
                            enableAllAvailableRegions={values.enableAllAvailableRegions}
                            selectedRegions={values.selectedRegions}
                            disabled={fromDisabled || !isDraft}
                            regionsWithSufficientLanguages={snapshot.regionsWithSufficientLanguages}
                            setEnableAllAvailableRegions={onChange('enableAllAvailableRegions')}
                            setSelectedRegions={onChange('selectedRegions')}
                            errors={errors.selectedRegions}
                        />
                    </FormCard>

                    <FormCard
                        cardKey="main-settings"
                        cardTitle={t('main-settings')}
                        handleOpenCard={handleMobileCardClick}
                        openCardName={openCardName}
                    >
                        <BackendTypeField
                            skill={skill}
                            values={values}
                            onBackendTypeChange={onChange('backendType')}
                            onFunctionIdChange={onChange('functionId')}
                            onWebhookChange={onChange('uri')}
                            errors={errors}
                            disabled={fromDisabled}
                        />

                        <InstallableAppAccessField
                            installableAppAccess={values.skillAccess}
                            setInstallableAppAccess={onChange('skillAccess')}
                            disabled={fromDisabled}
                            channel={Channel.SmartHome}
                        />
                    </FormCard>

                    <FormCard
                        cardKey="catalog-publication"
                        cardTitle={t('publish-in-catalog')}
                        handleOpenCard={handleMobileCardClick}
                        openCardName={openCardName}
                    >
                        <Field required fieldTitle={tLabels('developer-name')} error={errors.developerName}>
                            <TextInput
                                className={styles.formElement}
                                value={values.developerName}
                                onUpdate={onChange('developerName')}
                                disabled={fromDisabled}
                                size="xl"
                            />
                        </Field>

                        <EmailField
                            value={values.email}
                            onChange={onChange('email')}
                            error={errors.email || (displayEmailVerificationError ? t('not-confirmed') : undefined)}
                            touched={touched.email}
                            isVerified={!displayEmailVerificationError && !errors.email}
                            disabled={fromDisabled}
                        />

                        <Field fieldTitle={tLabels('trusted-skill')} error={errors.isTrustedSmartHomeSkill}>
                            <SegmentedRadioGroup
                                className={styles.formButton}
                                value={String(values.isTrustedSmartHomeSkill)}
                                onUpdate={(value) => onChange('isTrustedSmartHomeSkill')(value === 'true')}
                                disabled={fromDisabled}
                                size="xl"
                                options={[
                                    { value: 'true', content: t('yes') },
                                    { value: 'false', content: t('no') },
                                ]}
                            />
                        </Field>

                        <Field
                            required
                            fieldTitle={tLabels('logo')}
                            fieldDescription={t('logo-caption')}
                            error={errors.logoId}
                        >
                            <AliceSkillLogoUploader
                                logoUrl={values.logoUrl}
                                onIdChange={onChange('logoId')}
                                onUrlChange={onChange('logoUrl')}
                                disabled={fromDisabled}
                                setErrors={setErrors}
                            />
                        </Field>

                        {!isYangoRequest && (
                            <Field
                                fieldTitle={tLabels('brand-verification-website')}
                                error={displayBrandVerificationError ? t('not-confirmed') : undefined}
                            >
                                <HostsSelect
                                    value={values.brandVerificationWebsite}
                                    onChange={onChange('brandVerificationWebsite')}
                                    error={errors.brandVerificationWebsite}
                                    disabled={fromDisabled}
                                />
                            </Field>
                        )}
                    </FormCard>

                    {displayUserAgreements() && (
                        <FormCard
                            cardKey="user-agreements"
                            cardTitle={tAppForm('agreement')}
                            handleOpenCard={handleMobileCardClick}
                            openCardName={openCardName}
                        >
                            <SkillUserAgreement
                                agreements={values.skillUserAgreements}
                                onChange={onChange('skillUserAgreements')}
                                disabled={fromDisabled}
                            />
                        </FormCard>
                    )}

                    {isDraft && (
                        <FormCard
                            cardKey="others"
                            cardTitle={tDraftControl('others')}
                            handleOpenCard={handleMobileCardClick}
                            openCardName={openCardName}
                        >
                            <Field
                                fieldTitle={tLabels('note-for-moderator')}
                                fieldDescription={t('note-for-moderator-caption')}
                                error={errors.noteForModerator}
                            >
                                <TextArea
                                    className={styles.formElement}
                                    value={values.noteForModerator}
                                    onUpdate={onChange('noteForModerator')}
                                    disabled={fromDisabled}
                                    rows={4}
                                    note={
                                        <span className={styles.grayNote}>
                                            {lengthConfig.noteForModerator - (values.noteForModerator?.length || 0)}
                                        </span>
                                    }
                                    size="xl"
                                />
                            </Field>
                        </FormCard>
                    )}
                </div>
                <SmartHomeModeration
                    aliceApp={skill}
                    safeToRequestReview={!hasUnsavedChanges}
                    afterReviewRequest={() => setHasUnsavedChanges && setHasUnsavedChanges(false)}
                    refreshAliceApp={refreshAliceApp || (() => {})}
                />
            </div>
        </ErrorBoundary>
    );
};

const SmartHomeForm = formContainer<OuterProps, SmartHomeFormSettings>({
    mapPropsToValues: (props) => mapSmartHomeSettingsToValues(props.settings),
    handleSubmit: async (values, props, { setErrors }) => {
        try {
            const settings = mapValuesToSmartHomeSettings(values);

            await updateDraft(props.skill.id, settings, props.skill.channel);
            addToast(props, { title: tGeneralSettings('changes-save-success'), theme: 'success' });

            if (!isEqual(props.fetchedUserAgreements, values.skillUserAgreements)) {
                await formUARequests(
                    props.skill.id,
                    props.fetchedUserAgreements ?? [],
                    values.skillUserAgreements,
                    props.skill.channel,
                );
            }
            if (props.refreshAliceApp) {
                await props.refreshAliceApp();
            }
            await props.refreshSnapshot();
        } catch (error) {
            await new Promise((resolve) => setErrors(createUpdateError(error, props.skill.channel), resolve));
            addToast(props, { title: tGeneralSettings('error'), theme: 'danger' });
            console.error(error);
            throw error;
        }
    },
    setupValidationSchema: (props) => {
        const validateAsAdmin = utils.validateAsAdmin(__appConfig.user.isAdmin, props.skill.developerType);
        return {
            developerName: (value: string) => ({
                developerName: validateDeveloperName(value, validateAsAdmin),
            }),
            // TODO: https://st.yandex-team.ru/PASKILLS-9350 Запрещать название навыка "Умный дом"
            name: (value: string) => ({ name: validateSmartHomeName(value, validateAsAdmin) }),
            supportedLanguages: (value: SupportedLanguageFormData[]) => {
                const validationResult = [];
                const languagesAlreadyPresent = new Set<LanguageCode>();
                let errorIsPresent = false;

                if (value.length === 0) {
                    return { supportedLanguages: [JSON.stringify({})] };
                }

                for (const language of value) {
                    const errorsForLanguageForm: ErrorForLanguageForm = {};

                    // check selected language
                    if (language.languageCode === undefined) {
                        errorsForLanguageForm.languageDisplayName = t('error-validation-language-not-selected');
                    } else if (languagesAlreadyPresent.has(language.languageCode)) {
                        errorsForLanguageForm.languageDisplayName = t('error-validation-language-duplicate');
                    } else {
                        languagesAlreadyPresent.add(language.languageCode);
                    }

                    if (
                        language.appName === undefined ||
                        language.appName === '' ||
                        language.appName.length < MIN_NAME_LENGTH
                    ) {
                        errorsForLanguageForm.appName = t('error-validation-min-length-dialog-name', {
                            number: MIN_NAME_LENGTH,
                        });
                    }

                    const validateDialogName = validateSmartHomeName(language.appName || '', validateAsAdmin);

                    if (validateDialogName) {
                        errorsForLanguageForm.appName = validateDialogName;
                    }

                    if (
                        language.externalSettingsDescription === undefined ||
                        language.externalSettingsDescription === ''
                    ) {
                        errorsForLanguageForm.externalSettingsDescription = t('error-validation-empty-description');
                    } else if (validateDescription(language.externalSettingsDescription)) {
                        errorsForLanguageForm.externalSettingsDescription = t('error-validation-description');
                    }

                    if (!(language.supportedUnitsDescription || '').trim()) {
                        errorsForLanguageForm.supportedUnitsDescription = t('error-validation-empty-description');
                    } else if (validateDescription(language.supportedUnitsDescription || '')) {
                        errorsForLanguageForm.supportedUnitsDescription = t('error-validation-description');
                    }

                    if (language.supportServiceUrlIsRequired && !language.supportServiceUrl) {
                        errorsForLanguageForm.supportServiceUrl = tSettings('ua-validity-url-req');
                    }
                    if (!!language.supportServiceUrl && validateURL(language.supportServiceUrl)) {
                        errorsForLanguageForm.supportServiceUrl = t('error-validation-url');
                    }

                    if (
                        language.subtitle !== undefined &&
                        validateLength(language.subtitle, lengthConfig.secondaryTitle, 'field-subtitle')
                    ) {
                        errorsForLanguageForm.subtitle = t('error-validation-subtitle');
                    }

                    if (Object.keys(errorsForLanguageForm).length !== 0) {
                        errorIsPresent = true;
                    }

                    validationResult.push(JSON.stringify(errorsForLanguageForm));
                }
                return { supportedLanguages: errorIsPresent ? validationResult : undefined };
            },
            selectedRegions: (regions: RegionCode[]) => ({
                selectedRegions: regions.length === 0 ? [t('error-regions-not-selected')] : undefined,
            }),
            uri: (value: string) => ({
                uri: validateURL(value, props.skillUseZora) && t('error-validation-url'),
            }),
            brandVerificationWebsite: (value: string) => ({
                brandVerificationWebsite: validateURL(value) && t('error-validation-url'),
            }),
            description: () => ({
                description: '',
            }),
            noteForModerator: (value: string) => ({
                noteForModerator: validateNote(value) && t('error-validation-note-moderator'),
            }),
            email: (value: string) => ({
                email: validateEmail(value) && t('error-validation-email'),
            }),
            secondaryTitle: () => ({
                secondaryTitle: '',
            }),
        };
    },
})(SmartHomeFormFields);

export const SmartHomeAppForm = withSnapshot(withToaster<OuterProps & FormContainerProps>()(SmartHomeForm));
